import { ModalType } from '@/Enums/UserManagement/ModalType.enum';
import { Role } from '@/Enums/Users/Role.enum';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import { getNewUserDefaultValue } from '@/helpers/userManagement/form';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddNewUserModal from './AddNewUserModal';
import ViewProfileModal from './ViewProfileModal';

const UserModal = ({ open, handleClose, meRole, meWhitelabel, modalType }) => {
  const { selectedUser, selectedUserRequestId, screensByRole } = useSelector(state => state.userManagement);
  const [loading, setLoading] = useState(false);

  const formMethods = useForm({
    defaultValues: useMemo(() => {
      if (modalType === ModalType.View) {
        return selectedUser;
      }
      return getNewUserDefaultValue(meRole, meWhitelabel);
    }, [meRole, meWhitelabel, modalType, selectedUser]),
  });

  const { watch, reset, resetField, setValue } = formMethods;

  useEffect(() => {
    if (!watch) return;
    const subscription = watch((value, { name, type }) => {
      if (name === 'userRole' && type === 'change') {
        const isOriginalRole = value?.userRole == selectedUser?.userRole;
        if (modalType === ModalType.New || isOriginalRole) {
          ['organization', 'whitelabel', 'clients', 'client', 'agency', 'advertiser'].map(field => resetField(field));
        } else {
          ['organization', 'whitelabel', 'client', 'agency', 'advertiser'].map(field => setValue(field, ''));
          setValue('clients', []);
        }
        return;
      }
      if (value.userRole === Role.WHITELABEL_ADMIN && name === 'organization' && type === 'change') {
        setValue('whitelabel', '');
        return;
      }
      if (value.userRole === Role.CLIENT && name === 'whitelabel' && type === 'change') {
        setValue('clients', []);
        return;
      }
      if (value.userRole === Role.ADVERTISER && name === 'client' && type === 'change') {
        setValue('advertiser', '');
        return;
      }
      if (value.userRole === Role.AGENCY && name === 'client' && type === 'change') {
        setValue('agency', '');
        return;
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, resetField, selectedUser, setValue, modalType]);

  const userRole = +watch('userRole');

  const onModalClose = (_, reason) => {
    if (reason) return;
    handleClose();
    reset();
  };

  const commonModalProps = {
    open,
    handleClose,
    meRole,
    setLoading,
    formMethods,
    userRole,
    onModalClose,
    screensByRole,
  };

  switch (modalType) {
    case ModalType.New:
      return (
        <LoadingBackdropWrapper backdropOpen={loading}>
          <AddNewUserModal {...commonModalProps} />
        </LoadingBackdropWrapper>
      );
    case ModalType.View:
      return (
        <LoadingBackdropWrapper backdropOpen={loading}>
          <ViewProfileModal
            {...commonModalProps}
            selectedUser={selectedUser}
            selectedUserLoading={!!selectedUserRequestId}
          />
        </LoadingBackdropWrapper>
      );
    default:
      return null;
  }
};

UserModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  meRole: PropTypes.number,
  meWhitelabel: PropTypes.number,
  modalType: PropTypes.oneOf([ModalType.New, ModalType.View]),
};

export default UserModal;
